import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Button, FormControl, Radio, RadioGroup, FormControlLabel, FormLabel,
    TextField, Card, CardContent, Box, Typography, Snackbar, SnackbarContent,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Paper, Chip, Badge
} from "@material-ui/core";
import NoteService from "./Locality/Service/noteService";
import SchoolService from "./Locality/Service/schoolService";
import SocketService from "./Locality/Service/socketService";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import Widget from "../../components/Widget/Widget";

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    yellowButton: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        width: '100%',
        marginTop: '24px',
        '&:hover': {
            backgroundColor: '#FFB300',
        },
    },
    sectionTitle: {
        color: '#FFC107',
        fontWeight: 'bold',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '8px',
    },
    formCard: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '24px',
        marginBottom: '24px',
    },
    notesListContainer: {
        maxWidth: '800px',
        margin: '0 auto',
    },
    noteBubble: {
        padding: '12px 16px',
        borderRadius: 18,
        marginBottom: 16,
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        color: '#856404',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#ffe082',
            transform: 'scale(1.01)',
            transition: 'all 0.2s ease',
        },
    },
    noteHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        flexWrap: 'wrap',
    },
    noteType: {
        fontWeight: 700,
        fontSize: '0.8rem',
        marginBottom: 6,
    },
    noteText: {
        fontSize: '0.9rem',
        marginBottom: 6,
    },
    noteTime: {
        fontSize: '0.7rem',
        opacity: 0.75,
    },
    chatDialog: {
        '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '90vw',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    chatHeader: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#f5f5f5',
    },
    chatMessageBubble: {
        padding: '10px 16px',
        borderRadius: 18,
        marginBottom: 12,
        maxWidth: '70%',
        wordWrap: 'break-word',
    },
    messageSent: {
        backgroundColor: '#2196F3',
        color: 'white',
        marginLeft: 'auto',
        textAlign: 'right',
    },
    messageReceived: {
        backgroundColor: '#e0e0e0',
        color: '#000',
        marginRight: 'auto',
    },
    formNoteBubble: {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        color: '#856404',
    },
    chatInput: {
        padding: 16,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
}));

export default function Note() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);
    const [schoolName, setSchoolName] = useState("");
    const [toast, setToast] = useState({
        open: false,
        type: 'success',
        message: '',
    });
    const [notesList, setNotesList] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const chatMessagesEndRef = useRef(null);
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const lastReadTimesRef = useRef({}); // { noteId: timestamp }
    const pollingIntervalRef = useRef(null);
    const socketInitializedRef = useRef(false);

    useEffect(() => {
        if (userDetails && (userDetails.role === 'SCHOOLE' || userDetails.role === 'TEACHER' || userDetails.role === 'PARENT')) {
            const schooleId = userDetails.schooleId || userDetails.schoolId;
            if (schooleId) {
                SchoolService.getSchooleRegistrationById(schooleId)
                    .then((res) => {
                        if (res && res.schooleName) {
                            setSchoolName(res.schooleName);
                        }
                    })
                    .catch((err) => {
                        console.error('Error fetching school details:', err);
                    });
            }
        }
        loadNotes();
        
        // Load last read times from localStorage
        const savedLastReadTimes = localStorage.getItem('noteLastReadTimes');
        if (savedLastReadTimes) {
            try {
                lastReadTimesRef.current = JSON.parse(savedLastReadTimes);
            } catch (e) {
                console.error('Error parsing last read times:', e);
            }
        }
        
        // Initialize socket connection
        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }
        
        // Start polling for new messages every 10 seconds (fallback if socket fails)
        pollingIntervalRef.current = setInterval(() => {
            // Use setState callback to get latest notesList
            setNotesList(currentNotes => {
                checkForUnreadMessages(currentNotes);
                return currentNotes;
            });
        }, 10000);
        
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            // Disconnect socket on unmount
            SocketService.disconnectSocket();
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [note, setNote] = useState({
        noteType: '',
        note: '',
    });

    const validationSchema = Yup.object().shape({
        noteType: Yup.string().required('Please select Note Type'),
        note: Yup.string(),
    });

    const formik = useFormik({
        initialValues: note,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            const submitData = {
                noteType: values.noteType,
                note: values.note || '',
            };

            NoteService.createNote(submitData).then((res) => {
                resetForm();
                setNote({
                    noteType: '',
                    note: '',
                });
                setToast({
                    open: true,
                    type: 'success',
                    message: 'Message sent successfully.',
                });
                loadNotes(); // Refresh notes list
            }).catch((err) => {
                setToast({
                    open: true,
                    type: 'error',
                    message: err.response?.data?.message || 'Error sending message',
                });
            });
        },
    });

    const loadNotes = async () => {
        try {
            const res = await NoteService.getAllNotes();
            const notes = res || [];
            setNotesList(notes);
            // Check for unread messages after loading notes
            checkForUnreadMessages(notes);
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const checkForUnreadMessages = async (notesToCheck = null) => {
        try {
            // Use provided notes or current state
            const currentNotes = notesToCheck || notesList;
            if (!currentNotes || currentNotes.length === 0) return;
            
            const counts = {};
            const currentUserId = userDetails?._id;
            
            for (const note of currentNotes) {
                if (!note?._id) continue;
                
                try {
                    const messages = await NoteService.getChatMessagesByNoteId(note._id);
                    const allMessages = Array.isArray(messages) ? messages : (messages?.data || []);
                    
                    // Get last read time for this note
                    const lastReadTime = lastReadTimesRef.current[note._id] 
                        ? new Date(lastReadTimesRef.current[note._id])
                        : null;
                    
                    // Count unread messages (chat messages only, not form notes)
                    const unreadCount = allMessages.filter(msg => {
                        // Only count chat messages (not form notes)
                        if (msg.messageKind !== 'CHAT') return false;
                        // Only count messages from others (not from current user)
                        const msgSenderId = msg?.createdBy?._id || msg?.createdBy;
                        if (currentUserId && msgSenderId && String(currentUserId) === String(msgSenderId)) {
                            return false;
                        }
                        // Count if message is newer than last read time
                        if (lastReadTime) {
                            return new Date(msg.createdAt) > lastReadTime;
                        }
                        // If never read, count all messages from others
                        return true;
                    }).length;
                    
                    if (unreadCount > 0) {
                        counts[note._id] = unreadCount;
                    }
                } catch (err) {
                    console.error(`Error checking unread for note ${note._id}:`, err);
                }
            }
            setUnreadCounts(counts);
        } catch (err) {
            console.error('Error checking for unread messages:', err);
        }
    };

    const loadChatMessages = async (noteId) => {
        try {
            if (!noteId) {
                console.error('No noteId provided to loadChatMessages');
                return;
            }
            console.log('Loading chat messages for noteId:', noteId);
            const res = await NoteService.getChatMessagesByNoteId(noteId);
            console.log('Chat messages response:', res);
            // httpService already extracts response.data, so res is the array directly
            const messages = Array.isArray(res) ? res : (res?.data || []);
            setChatMessages(messages);
            console.log('Set chat messages:', messages);
            
            // Scroll to bottom after messages load
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error fetching chat messages:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error loading chat messages',
            });
        }
    };

    const handleOpenChat = async (note) => {
        console.log('Opening chat for note:', note);
        if (!note?._id) {
            console.error('Note does not have _id:', note);
            setToast({
                open: true,
                type: 'error',
                message: 'Invalid note selected',
            });
            return;
        }
        setSelectedNote(note);
        setChatOpen(true);
        
        // Load initial chat messages
        await loadChatMessages(note._id);
        
        // Join socket room for real-time updates
        SocketService.joinNoteChat(note._id, {
            onJoined: (data) => {
                console.log('Joined room:', data);
            },
            onMessage: (messageData) => {
                console.log('Received real-time message:', messageData);
                // Add new message to chat
                setChatMessages(prev => {
                    // Check if message already exists (avoid duplicates)
                    const exists = prev.some(msg => msg._id === messageData._id);
                    if (exists) return prev;
                    return [...prev, messageData];
                });
                
                // Auto-scroll to bottom
                setTimeout(() => {
                    if (chatMessagesEndRef.current) {
                        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            },
            onError: (error) => {
                console.error('Socket error:', error);
                setToast({
                    open: true,
                    type: 'error',
                    message: error.message || 'Error connecting to chat',
                });
            },
        });
        
        // Mark as read - update last read time
        const now = new Date().toISOString();
        lastReadTimesRef.current[note._id] = now;
        localStorage.setItem('noteLastReadTimes', JSON.stringify(lastReadTimesRef.current));
        
        // Clear unread count for this note
        setUnreadCounts(prev => {
            const updated = { ...prev };
            delete updated[note._id];
            return updated;
        });
    };

    const handleCloseChat = () => {
        // Leave socket room
        if (selectedNote?._id) {
            SocketService.leaveNoteChat(selectedNote._id);
        }
        setChatOpen(false);
        setSelectedNote(null);
        setChatMessages([]);
        setChatInput('');
    };

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!selectedNote?._id) return;

            const studentId = selectedNote?.studentId?._id || selectedNote?.studentId || userDetails?.studentId;
            if (!studentId) return;

            setChatInput('');
            
            // Try sending via socket first (real-time)
            if (SocketService.isConnected()) {
                SocketService.sendMessage(selectedNote._id, text, {
                    onSuccess: () => {
                        console.log('Message sent via socket');
                        // Message will be added via socket receive-message event
                        // No need to refresh notes list - socket handles it
                    },
                    onError: (error) => {
                        console.error('Socket send error, falling back to REST:', error);
                        // Fallback to REST API
                        sendViaRestAPI(studentId, text);
                    },
                });
            } else {
                // Fallback to REST API if socket not connected
                await sendViaRestAPI(studentId, text);
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const sendViaRestAPI = async (studentId, text) => {
        try {
            await NoteService.sendChatMessage({ 
                studentId, 
                note: text,
                parentNoteId: selectedNote._id
            });
            
            // Only reload messages and refresh notes list when using REST API
            await loadChatMessages(selectedNote._id);
            await loadNotes();
            
            // Scroll to bottom after sending
            setTimeout(() => {
                if (chatMessagesEndRef.current) {
                    chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } catch (err) {
            console.error('Error sending via REST API:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => {
        const me = userDetails?._id;
        const sender = msg?.createdBy?._id;
        return Boolean(me && sender && String(me) === String(sender));
    };

    return (
        <>
            <div className={classes.yellowHeader}>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                    {schoolName ? `  ${schoolName}` : ''}
                </Typography>
            </div>
            <Card className={classes.formCard}>
                <CardContent>
                    <form onSubmit={formik.handleSubmit}>
                        <FormControl component="fieldset" fullWidth style={{ marginBottom: '24px' }}>
                            <FormLabel component="legend" className={classes.sectionTitle}>
                                <span style={{ fontSize: '20px' }}>📋</span>
                                Note Type*
                            </FormLabel>
                            <RadioGroup
                                name="noteType"
                                value={formik.values.noteType}
                                onChange={formik.handleChange}
                                style={{ marginTop: '8px' }}
                            >
                                <FormControlLabel
                                    value="Request for in person meeting with teacher"
                                    control={<Radio color="primary" />}
                                    label="Request for in person meeting with teacher"
                                />
                                <FormControlLabel
                                    value="Request for a call post school hours"
                                    control={<Radio color="primary" />}
                                    label="Request for a call post school hours"
                                />
                               
                                <FormControlLabel
                                    value="Other"
                                    control={<Radio color="primary" />}
                                    label="Other"
                                />
                            </RadioGroup>
                            {formik.touched.noteType && formik.errors.noteType && (
                                <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: 6 }}>
                                    {formik.errors.noteType}
                                </div>
                            )}
                        </FormControl>

                        <Box style={{ marginBottom: '24px' }}>
                            <FormLabel className={classes.sectionTitle} style={{ marginBottom: '8px' }}>
                                Message (Optional)
                            </FormLabel>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                name="note"
                                value={formik.values.note}
                                onChange={formik.handleChange}
                                placeholder="Enter your message here..."
                                variant="outlined"
                                style={{ marginTop: '8px' }}
                            />
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            className={classes.yellowButton}
                            disabled={!formik.values.noteType}
                        >
                            SEND
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Notes List */}
            <Box className={classes.notesListContainer}>
                <Widget>
                    <Box p={2}>
                        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
                            Your Messages
                        </Typography>
                        {notesList.length === 0 ? (
                            <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                <Typography variant="body2">
                                    No messages yet. Create your first message above.
                                </Typography>
                            </Box>
                        ) : (
                            notesList.map((note) => {
                                const unreadCount = unreadCounts[note._id] || 0;
                                return (
                                    <Badge
                                        key={note._id}
                                        badgeContent={unreadCount > 0 ? unreadCount : 0}
                                        color="error"
                                        overlap="rectangular"
                                        style={{ width: '100%', marginBottom: 16 }}
                                    >
                                        <Box 
                                            className={classes.noteBubble}
                                            onClick={() => handleOpenChat(note)}
                                            style={{ width: '100%' }}
                                        >
                                            <Box className={classes.noteHeader}>
                                                {note.noteType && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        style={{
                                                            backgroundColor: '#4caf50',
                                                            color: 'white',
                                                            fontSize: '0.65rem',
                                                            height: '18px',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                    {note.noteType && (
                                        <Typography className={classes.noteType}>
                                            📋 {note.noteType}
                                        </Typography>
                                    )}
                                    {note.note && (
                                        <Typography className={classes.noteText}>
                                            {note.note}
                                        </Typography>
                                    )}
                                            <Typography className={classes.noteTime}>
                                                {formatTime(note.createdAt)}
                                            </Typography>
                                        </Box>
                                    </Badge>
                                );
                            })
                        )}
                    </Box>
                </Widget>
            </Box>

            {/* Chat Dialog */}
            <Dialog
                open={chatOpen}
                onClose={handleCloseChat}
                className={classes.chatDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.chatHeader}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                        Chat
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseChat}
                        style={{ color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.chatMessages}>
                    {chatMessages.length === 0 ? (
                        <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                            <Typography variant="body2">
                                No messages yet. Start a conversation!
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {chatMessages.map((m, idx) => {
                                // Check if it's a form note (has noteType and is not a CHAT message)
                                const isFormNote = m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                return (
                                    <Box
                                        key={m._id || idx}
                                        className={`${classes.chatMessageBubble} ${
                                            isMine(m) ? classes.messageSent : classes.messageReceived
                                        }`}
                                        style={isFormNote ? { backgroundColor: '#fff3cd', border: '1px solid #ffc107', color: '#856404' } : {}}
                                    >
                                        {isFormNote && (
                                            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, fontSize: '0.7rem', opacity: 0.9 }}>
                                                    📋 {m.noteType}
                                                </Typography>
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#4caf50',
                                                        color: 'white',
                                                        fontSize: '0.65rem',
                                                        height: '18px',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                        <Typography variant="caption" style={{ display: 'block', marginTop: 4, opacity: 0.75, fontSize: '0.7rem' }}>
                                            {formatTimeShort(m.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            <div ref={chatMessagesEndRef} />
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.chatInput}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type a message..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && chatInput.trim()) {
                                handleSendMessage();
                            }
                        }}
                        size="small"
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!chatInput.trim()}
                    >
                        <SendIcon />
                    </IconButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
